'use client';

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, ChevronDown, Plus, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { PhoneInput } from '@kit/ui/phone-input';
import { Trans } from '@kit/ui/trans';

import { OtpSignUpSchema } from '../schemas/otp-sign-up.schema';
import type { SignupCustomField } from '../schemas/signup-custom-fields.schema';
import { EmailInput } from './email-input';
import {
  SignupCustomFieldsInput,
  refineSignupFields,
} from './signup-custom-fields-input';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';

type FormValues = z.infer<typeof OtpSignUpSchema>;

const MAX_CHILDREN = 5;

interface OtpSignUpFormProps {
  defaultValues?: {
    email: string;
    name?: string;
  };
  signupCustomFields?: SignupCustomField[];
  displayTermsCheckbox?: boolean;
  displayChildrenSection?: boolean;
  onSubmit: (params: FormValues) => unknown;
  loading: boolean;
  captchaLoading: boolean;
}

/**
 * Email-OTP sign-up form. Identical identity fields to PasswordSignUpForm
 * (name + email + phone + optional children) minus the password and entity-name
 * fields — verification happens via an emailed code.
 */
export function OtpSignUpForm({
  signupCustomFields = [],
  defaultValues,
  displayTermsCheckbox,
  displayChildrenSection = true,
  onSubmit,
  loading,
  captchaLoading,
}: OtpSignUpFormProps) {
  const [childrenOpen, setChildrenOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(OtpSignUpSchema.superRefine(refineSignupFields(signupCustomFields)),
    ),
    defaultValues: {
      name: defaultValues?.name ?? '',
      email: defaultValues?.email ?? '',
      phoneNumber: '',
      children: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'children',
  });

  const handleToggleChildren = () => {
    if (!childrenOpen) {
      setChildrenOpen(true);
      if (fields.length === 0) {
        append({ firstName: '', lastName: '' });
      }
    } else {
      setChildrenOpen(false);
    }
  };

  const handleAddChild = () => {
    if (fields.length < MAX_CHILDREN) {
      append({ firstName: '', lastName: '' });
    }
  };

  const handleRemoveChild = (index: number) => {
    remove(index);
    if (fields.length <= 1) {
      setChildrenOpen(false);
    }
  };

  return (
    <Form {...form}>
      <form
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col space-y-2.5'}>
          <FormField
            control={form.control}
            name={'name'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'common:name'} />
                </FormLabel>

                <FormControl>
                  <Input
                    data-test={'name-input'}
                    required
                    type="text"
                    placeholder=""
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'email'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'common:emailAddress'} />
                </FormLabel>

                <FormControl>
                  <EmailInput data-test={'email-input'} {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'phoneNumber'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'common:phoneNumber'} />
                </FormLabel>

                <FormControl>
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={loading}
                    placeholder="Enter phone number"
                    data-test="phone-input"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <SignupCustomFieldsInput
            fields={signupCustomFields}
            disabled={loading}
          />
        </div>

        {/* Children Section */}
        {displayChildrenSection && (
          <div>
            <button
              type="button"
              onClick={handleToggleChildren}
              className={
                'flex w-full items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 text-left transition-colors' +
                (childrenOpen
                  ? ' border-primary/30 bg-primary/5'
                  : ' border-muted-foreground/25 hover:border-primary/30 hover:bg-primary/5')
              }
            >
              <div className="bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md">
                <Plus
                  className={
                    'text-primary h-3.5 w-3.5 transition-transform' +
                    (childrenOpen ? ' rotate-45' : '')
                  }
                />
              </div>

              <div className="flex-1">
                <span className="text-primary text-sm font-medium">
                  <Trans
                    i18nKey={'auth:registeringChildren'}
                    defaults={'Registering children?'}
                  />
                </span>

                {childrenOpen && fields.length > 0 && (
                  <span className="text-muted-foreground block text-xs">
                    <Trans
                      i18nKey={'auth:childrenCount'}
                      defaults={'{{count}} children registered'}
                      values={{ count: fields.length }}
                    />
                  </span>
                )}
              </div>

              <ChevronDown
                className={
                  'text-muted-foreground h-4 w-4 shrink-0 transition-transform' +
                  (childrenOpen ? ' rotate-180' : '')
                }
              />
            </button>

            {childrenOpen && (
              <div className="bg-primary/5 border-primary/30 rounded-b-lg border border-t-0 border-dashed p-3">
                <p className="text-muted-foreground mb-3 text-xs">
                  <Trans
                    i18nKey={'auth:childrenHelperText'}
                    defaults={
                      "Add your children's names so you can book sessions for them later."
                    }
                  />
                </p>

                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-2">
                      <span className="border-border bg-background text-muted-foreground mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                        {index + 1}
                      </span>

                      <div className="flex flex-1 gap-2">
                        <FormField
                          control={form.control}
                          name={`children.${index}.firstName`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  placeholder={`First name`}
                                  className="h-9 text-sm"
                                  data-test={`child-first-name-${index}`}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`children.${index}.lastName`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  placeholder={`Last name`}
                                  className="h-9 text-sm"
                                  data-test={`child-last-name-${index}`}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveChild(index)}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 mt-1.5 rounded-md p-1.5 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleAddChild}
                  disabled={fields.length >= MAX_CHILDREN}
                  className="text-primary hover:text-primary/80 mt-2 flex items-center gap-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <Trans
                    i18nKey={'auth:addAnotherChild'}
                    defaults={'Add another child'}
                  />
                </button>
              </div>
            )}
          </div>
        )}

        <Button
          data-test={'auth-submit-button'}
          className={'w-full'}
          type="submit"
          disabled={loading || captchaLoading}
        >
          <If condition={captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:verifyingCaptcha'} />
            </span>
          </If>

          <If condition={loading && !captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:signingUp'} />
            </span>
          </If>

          <If condition={!loading && !captchaLoading}>
            <span
              className={
                'animate-in fade-in slide-in-from-bottom-24 flex items-center'
              }
            >
              <Trans i18nKey={'auth:signUpWithEmail'} />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500'
                }
              />
            </span>
          </If>
        </Button>

        <If condition={displayTermsCheckbox}>
          <TermsAndConditionsFormField />
        </If>
      </form>
    </Form>
  );
}
